import { useEffect, useRef, useState } from 'react'
import { useLibraryDeps } from './LibraryDeps'

/**
 * A thumbnail's displayable src, loaded once its element scrolls into view.
 * 'blocked' when OnlyFans media cannot be shown here (no account proxy).
 */
export function useMediaSrc(url: string) {
  const { loadMedia } = useLibraryDeps()
  const ref = useRef<HTMLElement | null>(null)
  const [src, setSrc] = useState<string | null>(null)
  const [blocked, setBlocked] = useState(false)

  useEffect(() => {
    setSrc(null)
    setBlocked(false)
    if (!url) return
    // Local device previews (composer upload) — no CDN proxy needed.
    if (url.startsWith('blob:') || url.startsWith('data:')) {
      setSrc(url)
      return
    }
    const el = ref.current
    if (!el) return
    let live = true
    // The tile went away (closed, another category) before its turn: drop the queued fetch.
    const gone = new AbortController()
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return
        io.disconnect()
        loadMedia(url, gone.signal)
          .then((s) => {
            if (!live) return
            if (s.kind === 'blocked') setBlocked(true)
            else setSrc(s.src)
          })
          .catch(() => live && setBlocked(true))
      },
      { rootMargin: '50% 0px' },
    )
    io.observe(el)
    return () => {
      live = false
      gone.abort()
      io.disconnect()
    }
  }, [url, loadMedia])

  return { ref, src, blocked }
}
